import type { Url } from '@/domain/types';
import { type CatalogCup, CupSize, isCupSize, isCupSizeString, isCupSystem, isCupType } from '@/domain/catalog/cup';
import { assertIs } from '@/util';
import { convertLinks } from '@/api/convertLinks';
import type { ApiCatalogComponentBase } from '@/api/catalog/base';
import { isLinerSize } from '@/domain/catalog/liner';
import { HeadSize } from '@/domain/catalog/head';

export type ApiCatalogCup = ApiCatalogComponentBase & {
    /** The outer diameter of the cup */
    outer_diameter: number;

    /** The outer diameter of the cup */
    liner_size_: string;

    /** Link to the component-representation on the API */
    collisionMask: Url;
};

const cupLinkMap = {
    self: 'self',
    collisionMask: 'region',
} as const;

/**
 * Convert a cup representation from the api into a catalog-cup
 */
export function catalogCup(apiCup: ApiCatalogCup): CatalogCup {
    const { name, system, type, size, outer_diameter, liner_size_ } = apiCup;
    assertIs(isCupSystem, system);
    assertIs(isCupType, type);
    assertIs(isCupSizeString, size);
    assertIs(isCupSize, outer_diameter);
    assertIs(isLinerSize, liner_size_);
    return {
        name,
        system,
        type,
        size,
        outerDiameter: outer_diameter,
        linerSize: liner_size_,
        recommendedHeadSize: recommendedHeadSize(outer_diameter),
        ...convertLinks(apiCup, cupLinkMap),
    };
}

function recommendedHeadSize(outerDiameter: CupSize): HeadSize {
    /*
        - Cups with a diameter 44 or less can only support a size 28 head.
        - For cups with a diameter between 46 and 50, the largest head they are compatible with
        is a size 32.
        - The largest head we currently support is 36, any cup over size 50 we support is
        compatible with a size 36 head
     */
    if (outerDiameter < CupSize.C46) return HeadSize.H28;
    if (outerDiameter < CupSize.C50) return HeadSize.H32;
    return HeadSize.H36;
}
